<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import adminAPI from '@/services/api'
import { useClaimStore, useNotificationStore } from '@/store';
import { Admin } from '@upclaim/sdk/src/data-contracts';
import { computePermissionForCurrentUser } from '@/utils/directives/v-permission';

const props = defineProps<{
  claimId: string;
  currentAdmin: Admin
}>();

onMounted(() => {
  watch(() => props.claimId, (claimId) => {
    if (claimId) {
      listAgents(claimId);
    }
  }, { immediate: true });

  watch(() => props.currentAdmin, (admin) => {
    selectedAgent.value = admin || defaultValue;
  }, { immediate: true });

  watch(selectedAgent, (admin) => selectAgent(admin));
});

async function listAgents(claimId) {
  const { admins } = await adminAPI.listTravelAgencyAgentsForClaim(claimId);
  if (admins) agents.value = [defaultValue, ...admins];
}

async function selectAgent(agent: Agent, save = false) {
  if (save) {
    const payload: any = agent.id
      ? { adminId: agent.id }
      : { unset: true };
    const {success} = await adminAPI.setTravelAgencyAgentForClaim(props.claimId, payload);
    if (success) {
      useNotificationStore().emitNotification({ title: "Agent enregistré" });
    }
  }
}

type Agent = { id: string | null; fullName: string; email: string | null };
const defaultValue: Agent = { id: null, fullName: 'Aucun', email: null };
const agents = ref<Agent[]>([ defaultValue ]);

const query = ref('')
const selectedAgent = ref();
const filteredAgents = computed(() =>
  query.value === ''
    ? agents.value
    : agents.value.filter((agent) => {
        return agent.fullName.toLowerCase().includes(query.value.toLowerCase())
      })
)
</script>
<template>
  <Combobox as="div" v-model="selectedAgent" @update:model-value="(data: any) => selectAgent(data, true)" :disabled="!computePermissionForCurrentUser(['UPCLAIM_ADMIN', 'CUSTOMER_ADMIN', 'CUSTOMER_AGENT'])">
    <!-- <ComboboxLabel class="block mb-2 text-sm font-medium leading-6 text-gray-900">Assigned to</ComboboxLabel> -->
    <div class="relative" v-tooltip="selectedAgent?.email">
      <ComboboxInput class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
        @change="query = $event.target.value" :display-value="(agent: any) => agent?.fullName" />
      <ComboboxButton class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none">
        <ChevronUpDownIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
      </ComboboxButton>

      <ComboboxOptions v-if="filteredAgents.length > 0" class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
        <ComboboxOption v-for="(agent, index) in filteredAgents" :key="index" :value="agent" as="template" v-slot="{ active, selected }">
          <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-blue-600 text-white' : 'text-gray-900']">
            <div class="flex">
              <span :class="['truncate', selected && 'font-semibold']">
                {{ agent.fullName }}
              </span>
              <!-- <span :class="['ml-2 truncate text-blue-50', active ? 'text-blue-200' : 'text-gray-500']">
                {{ agent.email }}
              </span> -->
            </div>

            <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-blue-600']">
              <CheckIcon class="w-5 h-5" aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>
  </Combobox>
</template>